import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function BiReports() {
  const navigate = useNavigate();
  const location = useLocation();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  
  // Determine active view from URL
  const activeView = location.pathname.includes('/reports') ? 'reports' : 'dashboard';
  
  // Dashboard stats
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalStaff: 0,
    totalFees: 0,
    paidFees: 0,
    unpaidFees: 0,
    totalDiscounts: 0,
    totalClasses: 0,
    totalParents: 0,
    attendanceRate: 0,
    presentToday: 0,
    absentToday: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [feesStatement, setFeesStatement] = useState([]);
  const [feesBalance, setFeesBalance] = useState([]);
  const [classWiseStats, setClassWiseStats] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
    
    // Redirect to dashboard if on base route
    if (location.pathname === '/school/bi-reports') {
      navigate('/school/bi-reports/dashboard', { replace: true });
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchDashboardStats();
      fetchFeesStatement();
      fetchFeesBalance();
      fetchClassWiseStats();
      fetchRecentActivities();
    }
  }, [schoolData?.id, selectedBranchId]);

  const fetchDashboardStats = async () => {
    try {
      setLoadingStats(true);
      let query = supabase
        .from('students')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { count: studentCount } = await query;

      // Get teachers count
      let teacherQuery = supabase
        .from('teachers')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        teacherQuery = teacherQuery.eq('branch_id', selectedBranchId);
      }

      const { count: teacherCount } = await teacherQuery;

      // Get staff count (receptionists + drivers + cooks)
      let staffQuery = supabase
        .from('receptionists')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        staffQuery = staffQuery.eq('branch_id', selectedBranchId);
      }

      const { count: receptionistCount } = await staffQuery;

      let driverQuery = supabase
        .from('transport_persons')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        driverQuery = driverQuery.eq('branch_id', selectedBranchId);
      }

      const { count: driverCount } = await driverQuery;

      let cookQuery = supabase
        .from('cooks')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        cookQuery = cookQuery.eq('branch_id', selectedBranchId);
      }

      const { count: cookCount } = await cookQuery;

      const totalStaff = (receptionistCount || 0) + (driverCount || 0) + (cookCount || 0);

      // Get fees data
      let feesQuery = supabase
        .from('student_fees')
        .select('amount, paid_amount, discount_amount, status')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        feesQuery = feesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: feesData } = await feesQuery;

      let totalFees = 0;
      let paidFees = 0;
      let unpaidFees = 0;
      let totalDiscounts = 0;

      if (feesData) {
        feesData.forEach(fee => {
          totalFees += parseFloat(fee.amount || 0);
          paidFees += parseFloat(fee.paid_amount || 0);
          totalDiscounts += parseFloat(fee.discount_amount || 0);
          if (fee.status === 'unpaid' || fee.status === 'partial') {
            unpaidFees += parseFloat(fee.amount || 0) - parseFloat(fee.paid_amount || 0) - parseFloat(fee.discount_amount || 0);
          }
        });
      }

      // Get classes count
      let classQuery = supabase
        .from('classes')
        .select('id', { count: 'exact', head: true })
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        classQuery = classQuery.eq('branch_id', selectedBranchId);
      }

      const { count: classCount } = await classQuery;

      // Get parents count - extract unique parents from students table
      let parentCount = 0;
      try {
        let parentsQuery = supabase
          .from('students')
          .select('parent_email')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .not('parent_email', 'is', null);
        
        if (selectedBranchId) {
          parentsQuery = parentsQuery.eq('branch_id', selectedBranchId);
        }
        
        const { data: studentsWithParents, error: parentsError } = await parentsQuery;
        
        if (!parentsError && studentsWithParents && studentsWithParents.length > 0) {
          // Get unique parent emails (case-insensitive)
          const uniqueParentEmails = new Set();
          studentsWithParents.forEach(student => {
            if (student.parent_email) {
              uniqueParentEmails.add(student.parent_email.toLowerCase().trim());
            }
          });
          parentCount = uniqueParentEmails.size;
        }
        
        // Also try to count from parents table if it exists (fallback)
        try {
          let parentsTableQuery = supabase
            .from('parents')
            .select('id', { count: 'exact', head: true })
            .eq('school_id', schoolData.id);
          
          if (selectedBranchId) {
            // If parents table has branch_id, filter by it
            // Otherwise, filter by students' branch_id
            const { data: studentsWithBranch } = await supabase
              .from('students')
              .select('parent_id')
              .eq('school_id', schoolData.id)
              .eq('branch_id', selectedBranchId)
              .eq('is_active', true)
              .not('parent_id', 'is', null);
            
            if (studentsWithBranch && studentsWithBranch.length > 0) {
              const uniqueParentIds = [...new Set(studentsWithBranch.map(s => s.parent_id).filter(id => id))];
              if (uniqueParentIds.length > 0) {
                const { count: parentsTableCount } = await supabase
                  .from('parents')
                  .select('id', { count: 'exact', head: true })
                  .eq('school_id', schoolData.id)
                  .in('id', uniqueParentIds);
                // Use parents table count if it's higher (more accurate)
                if (parentsTableCount && parentsTableCount > parentCount) {
                  parentCount = parentsTableCount;
                }
              }
            }
          } else {
            const { count: parentsTableCount } = await parentsTableQuery;
            // Use parents table count if it's higher (more accurate)
            if (parentsTableCount && parentsTableCount > parentCount) {
              parentCount = parentsTableCount;
            }
          }
        } catch (parentsTableError) {
          // Parents table might not exist, that's okay - use student-based count
          console.log('Parents table not available, using student-based count');
        }
      } catch (error) {
        console.error('Error counting parents:', error);
        parentCount = 0;
      }

      // Get attendance stats for today
      const today = new Date().toISOString().split('T')[0];
      let attendanceQuery = supabase
        .from('attendance')
        .select('status')
        .eq('attendance_date', today);

      if (selectedBranchId) {
        // Get students in this branch first
        const { data: branchStudents } = await supabase
          .from('students')
          .select('id')
          .eq('school_id', schoolData.id)
          .eq('branch_id', selectedBranchId)
          .eq('is_active', true);
        
        if (branchStudents && branchStudents.length > 0) {
          const studentIds = branchStudents.map(s => s.id);
          attendanceQuery = attendanceQuery.in('student_id', studentIds);
        } else {
          setStats(prev => ({ ...prev, presentToday: 0, absentToday: 0, attendanceRate: 0 }));
        }
      }

      const { data: attendanceData } = await attendanceQuery;
      
      if (attendanceData) {
        const presentCount = attendanceData.filter(a => a.status === 'present').length;
        const absentCount = attendanceData.filter(a => a.status === 'absent').length;
        const totalToday = presentCount + absentCount;
        const attendanceRate = totalToday > 0 ? ((presentCount / totalToday) * 100).toFixed(1) : 0;

        setStats(prev => ({
          ...prev,
          presentToday: presentCount,
          absentToday: absentCount,
          attendanceRate: parseFloat(attendanceRate)
        }));
      }

      setStats(prev => ({
        ...prev,
        totalStudents: studentCount || 0,
        totalTeachers: teacherCount || 0,
        totalStaff: totalStaff,
        totalFees: totalFees,
        paidFees: paidFees,
        unpaidFees: unpaidFees,
        totalDiscounts: totalDiscounts,
        totalClasses: classCount || 0,
        totalParents: parentCount
      }));
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchFeesStatement = async () => {
    try {
      let query = supabase
        .from('student_fees')
        .select(`
          *,
          students:student_id (first_name, last_name, email),
          fee_schedules:fee_schedule_id (fee_name, amount)
        `)
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data } = await query;
      setFeesStatement(data || []);
    } catch (error) {
      console.error('Error fetching fees statement:', error);
    }
  };

  const fetchFeesBalance = async () => {
    try {
      let query = supabase
        .from('student_fees')
        .select(`
          *,
          students:student_id (first_name, last_name, email, class_id, section)
        `)
        .eq('school_id', schoolData.id)
        .in('status', ['unpaid', 'partial']);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data } = await query;
      
      const balanceData = (data || []).map(fee => ({
        ...fee,
        balance: parseFloat(fee.amount || 0) - parseFloat(fee.paid_amount || 0) - parseFloat(fee.discount_amount || 0)
      }));

      setFeesBalance(balanceData);
    } catch (error) {
      console.error('Error fetching fees balance:', error);
    }
  };

  const fetchClassWiseStats = async () => {
    try {
      let classQuery = supabase
        .from('classes')
        .select('id, class_name, sections')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        classQuery = classQuery.eq('branch_id', selectedBranchId);
      }

      const { data: classes } = await classQuery;

      if (!classes || classes.length === 0) {
        setClassWiseStats([]);
        return;
      }

      const classStats = await Promise.all(
        classes.map(async (cls) => {
          // Get student count for this class
          let studentQuery = supabase
            .from('students')
            .select('id', { count: 'exact', head: true })
            .eq('school_id', schoolData.id)
            .eq('class_id', cls.id)
            .eq('is_active', true);

          if (selectedBranchId) {
            studentQuery = studentQuery.eq('branch_id', selectedBranchId);
          }

          const { count: studentCount } = await studentQuery;

          // Get student IDs for this class
          let studentIdsQuery = supabase
            .from('students')
            .select('id')
            .eq('school_id', schoolData.id)
            .eq('class_id', cls.id)
            .eq('is_active', true);

          if (selectedBranchId) {
            studentIdsQuery = studentIdsQuery.eq('branch_id', selectedBranchId);
          }

          const { data: studentsInClass } = await studentIdsQuery;
          const studentIds = (studentsInClass || []).map(s => s.id);

          // Get fees for students in this class
          let feesQuery = supabase
            .from('student_fees')
            .select('amount, paid_amount, status')
            .eq('school_id', schoolData.id)
            .in('student_id', studentIds.length > 0 ? studentIds : [-1]);

          if (selectedBranchId) {
            feesQuery = feesQuery.eq('branch_id', selectedBranchId);
          }

          const { data: feesData } = await feesQuery;
          
          let totalFees = 0;
          let paidFees = 0;
          if (feesData) {
            feesData.forEach(fee => {
              totalFees += parseFloat(fee.amount || 0);
              paidFees += parseFloat(fee.paid_amount || 0);
            });
          }

          return {
            classId: cls.id,
            className: cls.class_name,
            sections: cls.sections || [],
            studentCount: studentCount || 0,
            totalFees: totalFees,
            paidFees: paidFees,
            unpaidFees: totalFees - paidFees
          };
        })
      );

      setClassWiseStats(classStats);
    } catch (error) {
      console.error('Error fetching class-wise stats:', error);
    }
  };

  const fetchRecentActivities = async () => {
    try {
      const activities = [];

      // Get recent student admissions (last 7 days)
      let recentStudentsQuery = supabase
        .from('students')
        .select('id, first_name, last_name, admission_date, created_at')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(5);

      if (selectedBranchId) {
        recentStudentsQuery = recentStudentsQuery.eq('branch_id', selectedBranchId);
      }

      const { data: recentStudents } = await recentStudentsQuery;
      if (recentStudents) {
        recentStudents.forEach(student => {
          activities.push({
            type: 'student_admission',
            title: 'New Student Admission',
            description: `${student.first_name} ${student.last_name} was admitted`,
            date: student.created_at,
            icon: '👤'
          });
        });
      }

      // Get recent fee payments (last 7 days)
      let recentFeesQuery = supabase
        .from('student_fees')
        .select(`
          id, paid_amount, updated_at,
          students:student_id (first_name, last_name)
        `)
        .eq('school_id', schoolData.id)
        .gt('paid_amount', 0)
        .gte('updated_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
        .order('updated_at', { ascending: false })
        .limit(5);

      if (selectedBranchId) {
        recentFeesQuery = recentFeesQuery.eq('branch_id', selectedBranchId);
      }

      const { data: recentFees } = await recentFeesQuery;
      if (recentFees) {
        recentFees.forEach(fee => {
          activities.push({
            type: 'fee_payment',
            title: 'Fee Payment Received',
            description: `${fee.students?.first_name || ''} ${fee.students?.last_name || ''} paid $${parseFloat(fee.paid_amount || 0).toFixed(2)}`,
            date: fee.updated_at,
            icon: '💰'
          });
        });
      }

      // Sort by date and limit to 10 most recent
      activities.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecentActivities(activities.slice(0, 10));
    } catch (error) {
      console.error('Error fetching recent activities:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = (data, filename) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(header => {
      const value = row[header];
      if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value);
      }
      return value || '';
    }));
    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
      />
      
      <SchoolSidebar schoolData={schoolData} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>{activeView === 'dashboard' ? 'BI & Reports - Dashboard' : 'BI & Reports - Reports'}</h1>
        </div>

        <div className="bi-reports-container">
          {/* Dashboard View */}
          {activeView === 'dashboard' && (
            <div className="bi-dashboard-content">
              <h2>Dashboard Overview</h2>

              {/* Overall Stats */}
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>Total Students</h3>
                  <p className="stat-value">{loadingStats ? '...' : stats.totalStudents}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Teachers</h3>
                  <p className="stat-value">{loadingStats ? '...' : stats.totalTeachers}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Staff</h3>
                  <p className="stat-value">{loadingStats ? '...' : stats.totalStaff}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Classes</h3>
                  <p className="stat-value">{loadingStats ? '...' : stats.totalClasses}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Parents</h3>
                  <p className="stat-value">{loadingStats ? '...' : stats.totalParents}</p>
                </div>
                <div className="stat-card">
                  <h3>Total Fees</h3>
                  <p className="stat-value">{loadingStats ? '...' : `$${stats.totalFees.toFixed(2)}`}</p>
                </div>
                <div className="stat-card">
                  <h3>Attendance Rate (Today)</h3>
                  <p className="stat-value">{loadingStats ? '...' : `${stats.attendanceRate}%`}</p>
                  <p style={{ fontSize: '0.875rem', color: '#6B7280', marginTop: '0.5rem' }}>
                    {stats.presentToday} Present / {stats.presentToday + stats.absentToday} Total
                  </p>
                </div>
                <div className="stat-card">
                  <h3>Paid Fees</h3>
                  <p className="stat-value" style={{ color: '#10B981' }}>
                    {loadingStats ? '...' : `$${stats.paidFees.toFixed(2)}`}
                  </p>
                </div>
                <div className="stat-card">
                  <h3>Unpaid Fees</h3>
                  <p className="stat-value" style={{ color: '#EF4444' }}>
                    {loadingStats ? '...' : `$${stats.unpaidFees.toFixed(2)}`}
                  </p>
                </div>
              </div>

              {/* Class-wise Statistics */}
              {classWiseStats.length > 0 && (
                <div className="dashboard-section">
                  <div className="section-header">
                    <h3>Class-wise Statistics</h3>
                    <div className="section-actions">
                      <button onClick={() => handleDownload(classWiseStats, 'class_wise_stats.csv')} className="btn-secondary">
                        Download
                      </button>
                      <button onClick={handlePrint} className="btn-secondary">Print</button>
                    </div>
                  </div>
                  <div className="table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'center' }}>Class Name</th>
                          <th style={{ textAlign: 'center' }}>Sections</th>
                          <th style={{ textAlign: 'center' }}>Students</th>
                          <th style={{ textAlign: 'center' }}>Total Fees</th>
                          <th style={{ textAlign: 'center' }}>Paid Fees</th>
                          <th style={{ textAlign: 'center' }}>Unpaid Fees</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classWiseStats.map((cls, idx) => (
                          <tr key={idx}>
                            <td>{cls.className}</td>
                            <td>{Array.isArray(cls.sections) ? cls.sections.join(', ') : cls.sections || 'N/A'}</td>
                            <td>{cls.studentCount}</td>
                            <td>${cls.totalFees.toFixed(2)}</td>
                            <td style={{ color: '#10B981' }}>${cls.paidFees.toFixed(2)}</td>
                            <td style={{ color: '#EF4444' }}>${cls.unpaidFees.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Activities */}
              {recentActivities.length > 0 && (
                <div className="dashboard-section">
                  <div className="section-header">
                    <h3>Recent Activities (Last 7 Days)</h3>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {recentActivities.map((activity, idx) => (
                      <div key={idx} style={{
                        padding: '1rem',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        border: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }}>
                        <div style={{ fontSize: '1.5rem' }}>{activity.icon}</div>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                            {activity.title}
                          </h4>
                          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6B7280' }}>
                            {activity.description}
                          </p>
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>
                          {new Date(activity.date).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Discounts */}
              <div className="dashboard-section">
                <div className="section-header">
                  <h3>View Discounts</h3>
                  <div className="section-actions">
                    <button onClick={() => handleDownload(feesStatement.filter(f => f.discount_amount > 0), 'discounts.csv')} className="btn-secondary">
                      Download
                    </button>
                    <button onClick={handlePrint} className="btn-secondary">Print</button>
                  </div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Student</th>
                        <th style={{ textAlign: 'center' }}>Fee Name</th>
                        <th style={{ textAlign: 'center' }}>Original Amount</th>
                        <th style={{ textAlign: 'center' }}>Discount</th>
                        <th style={{ textAlign: 'center' }}>Final Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feesStatement.filter(f => f.discount_amount > 0).map((fee, idx) => (
                        <tr key={idx}>
                          <td>{fee.students ? `${fee.students.first_name} ${fee.students.last_name}` : 'N/A'}</td>
                          <td>{fee.fee_schedules?.fee_name || 'N/A'}</td>
                          <td>${parseFloat(fee.amount || 0).toFixed(2)}</td>
                          <td>${parseFloat(fee.discount_amount || 0).toFixed(2)}</td>
                          <td>${(parseFloat(fee.amount || 0) - parseFloat(fee.discount_amount || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* View Fees Statement */}
              <div className="dashboard-section">
                <div className="section-header">
                  <h3>View Fees Statement</h3>
                  <div className="section-actions">
                    <button onClick={() => handleDownload(feesStatement, 'fees_statement.csv')} className="btn-secondary">
                      Download
                    </button>
                    <button onClick={handlePrint} className="btn-secondary">Print</button>
                  </div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Student</th>
                        <th style={{ textAlign: 'center' }}>Fee Name</th>
                        <th style={{ textAlign: 'center' }}>Amount</th>
                        <th style={{ textAlign: 'center' }}>Paid</th>
                        <th style={{ textAlign: 'center' }}>Discount</th>
                        <th style={{ textAlign: 'center' }}>Status</th>
                        <th style={{ textAlign: 'center' }}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feesStatement.map((fee, idx) => (
                        <tr key={idx}>
                          <td>{fee.students ? `${fee.students.first_name} ${fee.students.last_name}` : 'N/A'}</td>
                          <td>{fee.fee_schedules?.fee_name || 'N/A'}</td>
                          <td>${parseFloat(fee.amount || 0).toFixed(2)}</td>
                          <td>${parseFloat(fee.paid_amount || 0).toFixed(2)}</td>
                          <td>${parseFloat(fee.discount_amount || 0).toFixed(2)}</td>
                          <td><span className={`status-badge ${fee.status}`}>{fee.status}</span></td>
                          <td>{new Date(fee.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* View Fees Balance Summary */}
              <div className="dashboard-section">
                <div className="section-header">
                  <h3>View Fees Balance Summary</h3>
                  <div className="section-actions">
                    <button onClick={() => handleDownload(feesBalance, 'fees_balance.csv')} className="btn-secondary">
                      Download
                    </button>
                    <button onClick={handlePrint} className="btn-secondary">Print</button>
                  </div>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Student</th>
                        <th style={{ textAlign: 'center' }}>Class</th>
                        <th style={{ textAlign: 'center' }}>Fee Name</th>
                        <th style={{ textAlign: 'center' }}>Total Amount</th>
                        <th style={{ textAlign: 'center' }}>Paid</th>
                        <th style={{ textAlign: 'center' }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feesBalance.map((fee, idx) => (
                        <tr key={idx}>
                          <td>{fee.students ? `${fee.students.first_name} ${fee.students.last_name}` : 'N/A'}</td>
                          <td>{fee.students?.class_id || 'N/A'}</td>
                          <td>{fee.fee_schedules?.fee_name || 'N/A'}</td>
                          <td>${parseFloat(fee.amount || 0).toFixed(2)}</td>
                          <td>${parseFloat(fee.paid_amount || 0).toFixed(2)}</td>
                          <td>${fee.balance.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Paid Vs Unpaid Fees Summary */}
              <div className="dashboard-section">
                <div className="section-header">
                  <h3>Paid Vs Unpaid Fees Summary</h3>
                  <div className="section-actions">
                    <button onClick={handlePrint} className="btn-secondary">Print</button>
                  </div>
                </div>
                <div className="summary-cards">
                  <div className="summary-card paid">
                    <h4>Total Paid</h4>
                    <p className="summary-value">${stats.paidFees.toFixed(2)}</p>
                  </div>
                  <div className="summary-card unpaid">
                    <h4>Total Unpaid</h4>
                    <p className="summary-value">${stats.unpaidFees.toFixed(2)}</p>
                  </div>
                  <div className="summary-card total">
                    <h4>Total Fees</h4>
                    <p className="summary-value">${stats.totalFees.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Quick Communication */}
              <div className="dashboard-section">
                <h3>Quick Communication (Chats and Feedback)</h3>
                <p>Communication features will be available here. You can navigate to Notices section for announcements.</p>
                <button onClick={() => navigate('/school/notices')} className="btn-primary">
                  Go to Notices
                </button>
              </div>
            </div>
          )}

          {/* Reports View */}
          {activeView === 'reports' && (
            <div className="bi-reports-content">
              <h2>Reports</h2>
              
              <div className="reports-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '1.5rem',
                marginTop: '1.5rem'
              }}>
                <div className="report-card" onClick={() => navigate('/school/reports/student-report')}>
                  <h3>Student Report</h3>
                  <p>View and download student reports</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/staff-report')}>
                  <h3>Staff Report</h3>
                  <p>View and download staff reports</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/fee-report')}>
                  <h3>Fee Report</h3>
                  <p>View and download fee reports</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/transaction-report')}>
                  <h3>Transaction Report</h3>
                  <p>View and download transaction reports</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/class-list')}>
                  <h3>Class Lists</h3>
                  <p>View and download class lists</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/student-list')}>
                  <h3>Student List</h3>
                  <p>View and download student lists</p>
                </div>
                <div className="report-card" onClick={() => navigate('/school/reports/transport-list')}>
                  <h3>Transport List</h3>
                  <p>View and download transport lists</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BiReports;
